import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Trophy, Star } from "lucide-react";
import SubscriberBadge from "../SubscriberBadge";

interface SuccessLoaderProps {
 onComplete: () => void;
 type?: "subscription" | "purchase" | "video";
}

const SuccessLoader = ({ onComplete, type = "purchase" }: SuccessLoaderProps) => {
 const [progress, setProgress] = useState(0);
 const [isFinished, setIsFinished] = useState(false);

 useEffect(() => {
 const duration = 2500; // 2.5 seconds
 const interval = 25;
 const steps = duration / interval;
 const increment = 100 / steps;

 const timer = setInterval(() => {
 setProgress((prev) => {
 if (prev >= 100) {
 clearInterval(timer);
 setTimeout(() => {
 setIsFinished(true);
 setTimeout(onComplete, 2000);
 }, 500);
 return 100;
 }
 return prev + increment;
 });
 }, interval);

 return () => clearInterval(timer);
 }, [onComplete]);

 return (
 <motion.div 
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 exit={{ opacity: 0 }}
 className="fixed inset-0 z-[200] flex items-center justify-center bg-foreground/10 backdrop-blur-2xl"
 >
 <div className="relative flex flex-col items-center">
 {/* Animated Rings */}
 <div className="relative w-48 h-48 md:w-64 md:h-64 flex items-center justify-center">
 <svg className="w-full h-full -rotate-90">
 <circle
 cx="50%"
 cy="50%"
 r="45%"
 className="stroke-white/5 fill-none"
 strokeWidth="4"
 />
 <motion.circle
 cx="50%"
 cy="50%"
 r="45%"
 className="stroke-[#FF6A00] fill-none shadow-[0_0_20px_rgba(255,106,0,0.5)]"
 strokeWidth="8"
 strokeDasharray="100"
 strokeDashoffset={100 - progress}
 style={{ strokeLinecap: "round" }}
 />
 </svg>

 {/* Center Content */}
 <div className="absolute inset-0 flex flex-col items-center justify-center">
 <AnimatePresence mode="wait">
 {!isFinished ? (
 <motion.div
 key="loading"
 initial={{ opacity: 0, scale: 0.8 }}
 animate={{ opacity: 1, scale: 1 }}
 exit={{ opacity: 0, scale: 0.5 }}
 className="text-center"
 >
 <span className="text-4xl md:text-6xl font-black text-foreground font-display">
 {Math.round(progress)}%
 </span>
 <p className="text-[10px] md:text-xs font-black uppercase tracking-[0.3em] text-[#FF6A00] mt-2">
 Processando...
 </p>
 </motion.div>
 ) : (
 <motion.div
 key="success"
 initial={{ scale: 0, rotate: -20 }}
 animate={{ scale: 1, rotate: 0 }}
 transition={{ type: "spring", damping: 10, stiffness: 100 }}
 className="flex flex-col items-center"
 >
 {type === "subscription" ? (
 <div className="relative">
 <div className="absolute inset-0 bg-[#FF6A00] blur-3xl opacity-30 animate-pulse" />
 <SubscriberBadge className="text-4xl md:text-6xl scale-150" />
 </div>
 ) : (
 <div className="bg-[#FF6A00] p-6 rounded-full shadow-[0_0_50px_rgba(255,106,0,0.8)] border-4 border-foreground/10 ">
 <CheckCircle2 className="w-16 h-16 md:w-24 md:h-24 text-foreground" />
 </div>
 )}
 </motion.div>
 )}
 </AnimatePresence>
 </div>
 
 {/* Sparkles while loading */}
 {!isFinished && (
 <div className="absolute inset-0 pointer-events-none">
 {[...Array(6)].map((_, i) => (
 <motion.div
 key={i}
 initial={{ opacity: 0 }}
 animate={{ 
 opacity: [0, 1, 0],
 scale: [0, 1, 0],
 x: Math.random() * 200 - 100,
 y: Math.random() * 200 - 100
 }}
 transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.2 }}
 className="absolute left-1/2 top-1/2"
 >
 <Star className="w-3 h-3 text-[#FF6A00] fill-current" />
 </motion.div>
 ))}
 </div>
 )}
 </div>

 {/* Text Area */}
 <div className="mt-12 text-center max-w-sm px-6">
 <AnimatePresence mode="wait">
 {!isFinished ? (
 <motion.div
 key="t1"
 initial={{ y: 20, opacity: 0 }}
 animate={{ y: 0, opacity: 1 }}
 exit={{ y: -20, opacity: 0 }}
 >
 <h3 className="text-xl md:text-2xl font-black text-foreground uppercase tracking-tight">Liberando seu acesso</h3>
 <p className="text-sm text-muted-foreground mt-2">Estamos confirmando seu pagamento com segurança. Só um momento...</p>
 </motion.div>
 ) : (
 <motion.div
 key="t2"
 initial={{ y: 20, opacity: 0 }}
 animate={{ y: 0, opacity: 1 }}
 >
 <h3 className="text-2xl md:text-4xl font-black text-foreground uppercase tracking-tight">
 {type === "subscription" ? "VOCÊ É VIP!" : "PAGAMENTO APROVADO!"}
 </h3>
 <p className="text-primary font-bold mt-2 uppercase tracking-[0.2em] text-sm">
 {type === "subscription" ? "Assinatura Ativada" : "Conteúdo Liberado"}
 </p>
 
 <div className="mt-8 flex flex-col items-center gap-4">
 {type === "subscription" ? (
 <div className="bg-foreground/10 border border-foreground/10 rounded-2xl p-4 flex items-center gap-4">
 <Trophy className="w-8 h-8 text-[#FF6A00]" />
 <div className="text-left">
 <p className="text-xs font-black text-foreground uppercase">Novo Selo Conquistado</p>
 <p className="text-[10px] text-muted-foreground">Você agora possui acesso total ao app</p>
 </div>
 </div>
 ) : (
 <button 
 onClick={onComplete}
 className="bg-white text-black font-black uppercase text-xs tracking-widest px-8 h-12 rounded-full hover:bg-foreground/10 transition-all shadow-[0_0_20px_rgba(255,255,255,0.2)]"
 >
 Acessar Minhas Fotos Agora
 </button>
 )}
 <p className="text-[10px] text-foreground/40 uppercase font-bold tracking-widest animate-pulse">
 Redirecionando em instantes...
 </p>
 </div>
 </motion.div>
 )}
 </AnimatePresence>
 </div>
 </div>
 </motion.div>
 );
};

export default SuccessLoader;
